/**
 * **把一份视图翻译成动态音乐的输入。**
 *
 * 两个对局屏（热座 `game.ts` / 联机 `gameOnline.ts`）都要做同一件事，
 * 而它**必须是同一件事** —— 否则同一局在两种模式下音乐会不一样。
 * 所以收在这里，不复制。
 *
 * ## 这里为什么能只靠视图算出来
 *
 * 评分表要的全是**公开信息 + 我自己的手牌**：
 *
 * | 要什么 | 视图里在哪 |
 * | --- | --- |
 * | 我手上几张、几张是救命牌 | `yourHand`（只有本人有） |
 * | 开局人数 / 还活着几个 | `players`（公开） |
 * | 剩余抽牌堆 | `drawPileCount`（公开） |
 * | 对手总牌数 / 人数 | `players[].handCount`（公开，且**只有数量**） |
 * | 我是不是在濒死 | `yourDecision.kind === 'useRescue'` |
 *
 * ⚠️ 所以**客户端也算得出来** —— 不需要房主多发一个字节。
 * 这正是"投影"这套模型的附带好处：音乐要的东西刚好都在视图里。
 *
 * ⚠️ 热座那一屏用的是**操作者**的视图（`HotSeat.view` 就是
 * `project(state, actor)`），所以切座位时音乐会跟着换视角 ——
 * 这是对的：热座里"我"就是当前拿着设备的那个人。
 */

import { isRescue } from '../core/card';
import type { CardRegistry } from '../core/pack';
import type { PlayerView } from '../core/project';
import type { EnergyFacts, EnergyReporter } from './energy';

export function energyFactsOf(view: PlayerView, registry: CardRegistry): EnergyFacts {
  let rescueInHand = 0;
  for (const card of view.yourHand) {
    const def = registry.get(card.title);
    if (def !== undefined && isRescue(def)) rescueInHand += 1;
  }

  let opponentCards = 0;
  let opponentCount = 0;
  let aliveCount = 0;
  for (const player of view.players) {
    if (player.alive) aliveCount += 1;
    if (player.id === view.you) continue;
    opponentCards += player.handCount;
    if (player.alive) opponentCount += 1;
  }

  return {
    // ⚠️ 只在 `playing` 期间算分。`lobby` 与 `finished` 都算"不在对局里" ——
    //    评审：「在菜单总是为 0」「游戏结束后……energy 重置为 0」
    inGame: view.phase === 'playing',
    handSize: view.yourHand.length,
    rescueInHand,
    rosterSize: view.players.length,
    aliveCount,
    drawPileCount: view.drawPileCount,
    opponentCards,
    opponentCount,
    // ⚠️ 不止本人：旁观别人濒死时也要让濒死特效亮起来（红世界/故障/冻结）。
    //    公开的 pending 已经给出"谁正在做 useRescue"。
    dying:
      view.yourDecision?.kind === 'useRescue' ||
      view.pending?.kind === 'useRescue',
  };
}

/**
 * 把一份视图报上去。`reporter` 可能是 `undefined`（没有音频时就没人接）。
 *
 * 返回"算出来的那份事实" —— 冒烟测试与调试页要看它，而重算一遍
 * 只会让两处漂开。
 */
export function reportEnergy(
  reporter: EnergyReporter | undefined,
  view: PlayerView | null,
  registry: CardRegistry,
): EnergyFacts | null {
  if (view === null) return null;
  const facts = energyFactsOf(view, registry);
  reporter?.report(facts);
  return facts;
}
